import React, { useRef, useCallback } from 'react';
import { type Nomina } from '../../services/nomina/nomina.service';
import { X, Banknote, Calendar, User, CheckCircle, Clock, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import logoUrl from '../../assets/Logo.png';

interface Props {
  nomina: Nomina;
  onClose: () => void;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr: string) => {
  const fecha = dateStr.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || dateStr;
  return new Date(`${fecha}T00:00:00-05:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const ColillaPagoModal: React.FC<Props> = ({ nomina, onClose }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Base proporcional a los días (calculada por el backend)
  const baseQuincenal = nomina.totalDevengado - nomina.totalBonificaciones;
  const novedadesBonif = nomina.novedades.filter(n => n.tipo === 'HORA_EXTRA' || n.tipo === 'BONIFICACION' || n.tipo === 'FESTIVO');
  const novedadesDesc = nomina.novedades.filter(n => n.tipo === 'FALTA' || n.tipo === 'DESCUENTO_OTRO');

  const [isExporting, setIsExporting] = React.useState(false);

  const handleExportPDF = useCallback(async () => {
    if (!contentRef.current) return;
    setIsExporting(true);
    try {
      const element = contentRef.current;
      const dataUrl = await toPng(element, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;

      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
      });

      const imgHeight = (img.height * imgWidth) / img.width;
      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(dataUrl, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - margin * 2);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(dataUrl, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2);
      }

      const fileName = `Colilla_${nomina.usuario?.nombreCompleto?.replace(/\s+/g, '_') || 'Colaborador'}_${nomina.periodoInicio}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error exportando PDF:', error);
      alert('Error al generar el PDF. Intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  }, [nomina]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header con botón de PDF */}
        <div className="bg-emerald-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Banknote className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-bold">Colilla de Pago</h2>
                <p className="text-sm text-emerald-100">ConfiFarma Amiga</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg transition-colors text-sm font-medium"
                title="Descargar PDF"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Generando...' : 'PDF'}
              </button>
              <button onClick={onClose} className="p-2 hover:bg-emerald-700 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido capturable para PDF */}
        <div ref={contentRef} className="overflow-y-auto p-6 space-y-5 bg-white">
          {/* Logo */}
          <div className="flex justify-center pb-2">
            <img
              src={logoUrl}
              alt="ConfiFarma Amiga"
              className="h-16 object-contain"
            />
          </div>

          {/* Info colaborador */}
          <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gray-100 rounded-lg">
              <User className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 text-lg">{nomina.usuario?.nombreCompleto || '—'}</p>
              <p className="text-sm text-gray-500">{nomina.usuario?.email || '—'}</p>
            </div>
            <div>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                nomina.estado === 'PAGADA' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {nomina.estado === 'PAGADA' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                {nomina.estado === 'PAGADA' ? 'Pagada' : 'Pendiente'}
              </span>
            </div>
          </div>

          {/* Periodo */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Periodo: <strong className="text-gray-900">{formatDate(nomina.periodoInicio)} — {formatDate(nomina.periodoFin)}</strong></span>
          </div>

          {/* Condiciones laborales (trazabilidad) */}
          <div className="grid grid-cols-3 gap-3 text-center text-sm">
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Días laborados</p>
              <p className="font-bold text-gray-900">{nomina.diasLaborados}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Domingos</p>
              <p className="font-bold text-gray-900">{nomina.domingosTrabajados}</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Salario mensual</p>
              <p className="font-bold text-gray-900">{formatCurrency(nomina.salarioMensual)}</p>
            </div>
          </div>

          {/* Devengados */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Devengados</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Salario base quincenal</span>
                <span className="font-medium">{formatCurrency(baseQuincenal)}</span>
              </div>
              {novedadesBonif.map((n, i) => (
                <div key={i} className="flex justify-between py-1">
                  <span className="text-gray-600">
                    {n.tipo === 'HORA_EXTRA' ? 'Horas extra' : n.tipo === 'FESTIVO' ? 'Festivo' : 'Bonificación'}
                    {n.descripcion ? ` (${n.descripcion})` : ''}
                  </span>
                  <span className="font-medium text-emerald-600">+{formatCurrency(n.valorTotal)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2 border-t border-gray-100 font-semibold">
                <span>Total devengado</span>
                <span>{formatCurrency(nomina.totalDevengado)}</span>
              </div>
            </div>
          </div>

          {/* Descuentos */}
          {novedadesDesc.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Descuentos</h3>
              <div className="space-y-1.5 text-sm">
                {novedadesDesc.map((n, i) => (
                  <div key={i} className="flex justify-between py-1">
                    <span className="text-gray-600">{n.tipo === 'FALTA' ? 'Falta' : 'Descuento'} {n.descripcion ? `(${n.descripcion})` : ''} × {n.cantidad}</span>
                    <span className="font-medium text-red-500">-{formatCurrency(n.valorTotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 border-t border-gray-100 font-semibold text-red-600">
                  <span>Total descuentos</span>
                  <span>-{formatCurrency(nomina.totalDescuentos)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-bold text-emerald-800">TOTAL A PAGAR</p>
                <p className="text-xs text-emerald-600">Salario mensual referencia: {formatCurrency(nomina.salarioMensual)}</p>
              </div>
              <p className="text-3xl font-black text-emerald-700">{formatCurrency(nomina.totalPagar)}</p>
            </div>
          </div>

          {nomina.observaciones && (
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
              <strong>Observaciones:</strong> {nomina.observaciones}
            </div>
          )}

          {/* Footer del PDF */}
          <div className="text-center text-[10px] text-gray-400 pt-4 border-t border-gray-100">
            <p>ConfiFarma Amiga — Nómina generada el {new Date().toLocaleDateString('es-CO')}</p>
            <p>Este documento es una colilla informativa. Consérvelo para sus archivos.</p>
          </div>
        </div>
      </div>

    </div>
  );
};
